import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { auditService, ComplianceAuditEventDto } from '../../../services/audit.service';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';

/**
 * D2 (§11) — read-only compliance audit console. Lists the compliance_audit_events ledger (who did what,
 * when) for privileged actions: Identity-Review clear/reject, consent grant/withdraw, etc.
 */
const ENTITY_FILTERS = [
  { value: '', label: 'All' },
  { value: 'IdentityRelationship', label: 'Identity Review' },
  { value: 'Consent', label: 'Consent' },
];

export const AdminAuditConsole: React.FC = () => {
  const [rows, setRows] = useState<ComplianceAuditEventDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState('');

  const load = async (filter: string) => {
    setLoading(true);
    try {
      setRows(await auditService.list(filter || undefined));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load audit events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(entityType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType]);

  const badge = (action?: string | null) => {
    const a = (action || '').toUpperCase();
    if (a.includes('REJECT') || a.includes('WITHDRAWN')) return 'bg-[#fef2f2] text-[#b91c1c]';
    if (a.includes('CLEAR') || a.includes('GRANTED')) return 'bg-[#ecfdf5] text-[#10b981]';
    return 'bg-[#eff6ff] text-[#3b82f6]';
  };

  const fmt = (ts?: string | null) => (ts ? ts.replace('T', ' ').slice(0, 19) : '—');

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-[18px] font-bold text-slate-800 mb-0">Compliance Audit</h1>
          <p className="text-[12px] text-slate-500 mt-1 mb-0">
            Who did what, when — privileged actions traced for compliance (BRD §11). Read-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="text-[12px] border border-slate-200 rounded px-2 py-1.5 bg-white cursor-pointer focus:outline-none focus:border-[#1f4851]"
          >
            {ENTITY_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
          <button
            onClick={() => load(entityType)}
            className="px-3 py-1.5 text-[12px] font-semibold text-[#1f4851] border border-[#1f4851] rounded hover:bg-[#1f4851]/5 transition-colors cursor-pointer"
          >
            <i className="bi bi-arrow-clockwise mr-1.5"></i> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] px-4 py-10 text-center">
          <i className="bi bi-clipboard-check text-[28px] text-slate-300"></i>
          <div className="text-[13px] font-semibold text-slate-700 mt-2">No audit events</div>
          <div className="text-[11px] text-slate-400 mt-1">Privileged actions will appear here as they occur.</div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-[8px] font-bold text-slate-400 uppercase tracking-wider border-b border-[#e2e8f0]">
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Actor</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Entity</th>
                <th className="text-left px-3 py-2">Investor</th>
                <th className="text-left px-3 py-2">Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#e2e8f0] last:border-0 hover:bg-slate-50/50 transition-colors">
                  <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmt(r.createdAt)}</td>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-700">{r.actor || '—'}</div>
                    <div className="text-[9px] text-slate-400">{r.actorRole || ''}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${badge(r.action)}`}>
                      {r.action || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {r.entityType || '—'}{r.entityId ? <span className="text-slate-400"> #{r.entityId}</span> : null}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.investorUniqueId || '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{r.detail || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminAuditConsole;
